import { LearningSessionType, ResponseType } from 'src/analytics';
import {
  Correctness,
  SessionType,
  TemplateName,
  WebSessionType,
} from 'src/api/learningSessions/types';
import { TestPrompt } from '../learnableCards/TestLearnableCard/Prompt';
import { TestResponse } from '../learnableCards/TestLearnableCard/Response';
import {
  TEST_MAX_DELAY_TIMER_DURATION_IN_MS,
  TEST_MIN_DELAY_TIMER_DURATION_IN_MS,
  TEST_MIN_SPEED_DELAY_TIMER_DURATION_IN_MS,
} from '.';

export const getVideoOrAudioFileUrl = (prompt: TestPrompt) => {
  if (prompt.promptType === 'Audio') return prompt.audio;
  if (prompt.promptType === 'Video') return prompt.video;

  return null;
};

export const getPromptAndResponseDirection = (
  testResponse: TestResponse,
  testPrompt: TestPrompt,
) => {
  return {
    responseDirection: testResponse.direction,
    promptDirection: testPrompt.direction,
  };
};

/**
 * Firefox doesn't support reading the duration of .aac audio files and so
 * it returns large miscalculated values for the duration. The solution is to set a maximum
 * duration on the delay to avoid delaying for large unnecessary amounts of time (e.g. 100-second delay)
 */
export const getDelayLengthInMs = (
  durationInMs: number | null | typeof NaN,
  sessionType: SessionType,
) => {
  // at a minimum, the delay should be the TEST_MIN_DELAY_TIMER_DURATION_IN_MS
  const minDelayTime =
    sessionType === SessionType.SPEED_REVIEW
      ? TEST_MIN_SPEED_DELAY_TIMER_DURATION_IN_MS
      : TEST_MIN_DELAY_TIMER_DURATION_IN_MS;
  if (!durationInMs) return minDelayTime;

  if (durationInMs <= TEST_MAX_DELAY_TIMER_DURATION_IN_MS)
    return Math.max(durationInMs, minDelayTime);

  return TEST_MAX_DELAY_TIMER_DURATION_IN_MS;
};

// Just for all the tracking events
export const getLearningSessionType = (sessionType: WebSessionType): LearningSessionType => {
  const { AUDIO, REVIEW, MIXED } = SessionType;

  // SessionType and the generated LearningSessionType are mismatched
  if (sessionType === AUDIO) return LearningSessionType.audio;
  if (sessionType === REVIEW) return LearningSessionType.review;
  // The 1-0-1 analytics schemas have no `mixed` session type yet, so report mixed sessions as reviews
  if (sessionType === MIXED) return LearningSessionType.review;

  return LearningSessionType[sessionType] ?? LearningSessionType.unknown_session_type;
};

export const getAnalyticsParams = (
  response: TestResponse,
): {
  responseType: ResponseType;
  choices: string[];
  expectedAnswerChoices: string[];
} => {
  let responseType: ResponseType, choices: string[], expectedAnswerChoices: string[];

  switch (response.templateName) {
    case TemplateName.MULTIPLE_CHOICE:
    case TemplateName.REVERSED_MULTIPLE_CHOICE:
      responseType = ResponseType.multiple_choice;
      choices = response.choices;
      expectedAnswerChoices = [response.correctChoice];
      break;
    case TemplateName.TYPING:
      responseType = ResponseType.typing;
      choices = response.keyboardChoices;
      expectedAnswerChoices = response.answers;
      break;
    case TemplateName.TAPPING:
      responseType = ResponseType.tapping;
      choices = response.choices;
      expectedAnswerChoices = response.answers;
      break;
    case TemplateName.AUDIO_MULTIPLE_CHOICE:
      responseType = ResponseType.audio_multiple_choice;
      choices = response.choices;
      expectedAnswerChoices = [response.correctChoice];
      break;
    case TemplateName.TRACING:
      responseType = ResponseType.tracing;
      choices = [];
      expectedAnswerChoices = response.answers;
      break;
    /* istanbul ignore next: internal error exception */
    default:
      throw Error('Analytics event not implemented for this test type');
  }

  return { responseType, choices, expectedAnswerChoices };
};

export const getScoreFromCorrectness = (correctness: Correctness): number =>
  correctness === 'Correct' ? 1 : 0;

/**
 * Fully grown is if growth level is 6 or greater
 * of if growth level is 5 but the user got the answer correct
 * i.e. it is about to be fully grown after this event is accounted for
 */
export const willBeFullyGrownAfterTest = (growthLevel: number, score: number) =>
  growthLevel >= 6 || (growthLevel === 5 && score === 1);
